// translations/pages/kyveil/fi.js

export const kyveilPageFI = {
  "seo": {
    "title": "Kyveil: PII-anonymisointi | pAIoneer Labs GmbH",
    "description": "Kyveil tunnistaa ja anonymisoi henkilötiedot paikallisesti omissa järjestelmissäsi. Työpöytäsovellus tai REST API GDPR:n vaatimukset täyttävään henkilötietojen anonymisointiin.",
    "keywords": "Kyveil, PII-anonymisointi, henkilötietojen anonymisointi, tietosuoja, GDPR, Microsoft Presidio, spaCy, flair, paikallinen tietojenkäsittely",
    "ogTitle": "Kyveil: ammattimaista PII-anonymisointia tekoälyn ja NLP:n avulla",
    "ogDescription": "Henkilötietojen automaattinen tunnistus ja anonymisointi työpöytäsovelluksena tai REST API:na omassa infrastruktuurissasi.",
    "twitterTitle": "Kyveil: PII-anonymisointi",
    "twitterDescription": "Paikallinen henkilötietojen tunnistus ja anonymisointi arkaluonteisille dokumenteille.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Ohjelmisto henkilötietojen paikalliseen tunnistukseen ja anonymisointiin tekoälyn ja NLP:n avulla.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Tietosuoja ja anonymisointi",
    "title": "Kyveil",
    "subtitle": "Paikallinen anonymisointi ennen tekoälyä, testejä ja jakamista.",
    "description": "Kyveil on paikallinen anonymisointikerros asiakirjoille ja tekoälytyönkuluille. Ohjelmisto tunnistaa henkilötiedot Word-, Excel-, PDF- ja tekstitiedostoista, korvaa tai peittää ne ja näyttää esikatselun ennen kuin sisältö siirtyy tekoälykeskusteluihin, testeihin, analytiikkaan tai ulkoiseen jakoon. Kyveiliä kehitetään ja jatkokehitetään [IDD-ohjatusti](idd) (Intent-Driven Development) ja [Veridionilla](veridion) automatisoidusti tekoälytyöntekijöidemme toimesta ihminen loopissa.",
    "platforms": "Työpöytäsovellus, REST API ja AnonChat",
    "proofPoints": [
      "Dokumentit pysyvät paikallisesti omissa järjestelmissäsi.",
      "Henkilötiedot korvataan tai peitetään.",
      "Esikatselu näyttää anonymisoidun sisällön ennen jakamista tai tekoälychattia.",
      "Word, Excel, PDF ja teksti ovat keskeiset dokumenttityönkulut."
    ]
  },
  "dataSovereignty": {
    "title": "Sinun datasi. Sinun järjestelmäsi. Sinun hallintasi.",
    "subtitle": "Täysi datasuvereniteetti paikallisen käsittelyn ansiosta.",
    "items": [
      {
        "title": "Pilveä ei tarvita",
        "description": "Kaikki käsittely tapahtuu paikallisesti omassa infrastruktuurissasi. Tiedostosi eivät koskaan poistu järjestelmistäsi. Ei pilviriippuvuuksia, ei tietojen siirtoa ulkopuolelle."
      },
      {
        "title": "Reunalaskenta",
        "description": "Työpöytäsovellus toimii täysin itsenäisesti työasemilla. API otetaan käyttöön omilla palvelimillasi. Molemmat versiot käsittelevät kaiken paikallisesti oman tietoturva-alueesi sisällä."
      },
      {
        "title": "Data-autonomia",
        "description": "Täysi hallinta omiin tietovirtoihisi. Ei toimittajaloukkua. Ei jatkuvia pilvikustannuksia. Arkaluonteiset tietosi pysyvät yksinomaan sinun hallinnassasi."
      },
      {
        "title": "Tavallinen laitteisto",
        "description": "GPU:ta ei tarvita. Toimii tavallisilla toimistotietokoneilla ja vakiopalvelimilla. Erikoislaitteistoon ei tarvitse investoida."
      }
    ]
  },
  "features": {
    "title": "Keskeiset ominaisuudet",
    "subtitle": "Kaikki, mitä hallittu henkilötietojen tunnistus ja anonymisointi yrityksen työnkuluissa edellyttää.",
    "items": [
      {
        "title": "Tekoälypohjainen PII-tunnistus",
        "description": "Automaattinen tunnistus, jonka perustana ovat Microsoft Presidio SDK, spaCy (Explosion AI) ja flair (Humboldt-Universität zu Berlin).",
        "tone": "primary"
      },
      {
        "title": "Replace-tila",
        "description": "Korvaa tunnistetut henkilötiedot yleisillä paikkamerkeillä, kuten [NAME], [EMAIL] ja [ADDRESS], vaatimustenmukaisuutta edellyttäviin tilanteisiin.",
        "tone": "info"
      },
      {
        "title": "Fake-tila",
        "description": "Korvaa tunnistetut henkilötiedot uskottavilla korvaavilla arvoilla testiympäristöjä ja kehitystyötä varten.",
        "tone": "warning"
      },
      {
        "title": "Useita dokumenttimuotoja",
        "description": "Käsittelee Microsoft Word-, Excel-, OpenDocument- ja PDF-tiedostoja sekä pelkkää tekstiä.",
        "tone": "success"
      },
      {
        "title": "Monikielinen tuki",
        "description": "Sisäänrakennettu tuki saksalle, englannille, ranskalle, italialle ja espanjalle kielikohtaisilla NLP-malleilla.",
        "tone": "secondary"
      },
      {
        "title": "Mukautettava tunnistus",
        "description": "Määritä, mitkä PII-entiteetit tunnistetaan: nimet, osoitteet, sähköpostiosoitteet, puhelinnumerot, pankkitilit, verotunnisteet ja paljon muuta.",
        "tone": "primary"
      },
      {
        "title": "Laajennettavuus",
        "description": "Rajapinnat omalle esi- ja jälkikäsittelylogiikalle, jotta ratkaisu voidaan integroida juuri sinun työnkulkuihisi.",
        "tone": "info"
      },
      {
        "title": "CPU-optimoitu",
        "description": "Tehokas malliarkkitehtuuri toimii tavallisella toimistolaitteistolla ilman GPU:ta.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Teknologiapino",
    "subtitle": "Yritystason sovelluskehykset ja koeteltu NLP-tutkimus.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Alan standardiksi vakiintunut sovelluskehys henkilötietojen tunnistukseen ja anonymisointiin, mukana sisäänrakennetut entiteettitunnistimet.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Kehittäjä: Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Tuotantovalmis NLP-kirjasto, jonka esikoulutetut tilastolliset mallit tunnistavat nimettyjä entiteettejä (named entity recognition).",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Kehittäjä: Humboldt-Universität zu Berlin",
        "description": "Huipputason NLP-sovelluskehys, joka hyödyntää kontekstuaalisia merkkijonoupotuksia (contextual string embeddings). Edistyneet neuroverkkomallit parantavat entiteettien tunnistustarkkuutta.",
        "tone": "secondary"
      }
    ],
    "integration": "Integroitu tekoälyputki",
    "integrationDesc": "Yhdistetty tunnistus, jossa Presidio toimii orkestrointikerroksena ja spaCy- sekä flair-mallit takaavat parhaan mahdollisen tunnistuksen eri kielillä ja entiteettityypeillä."
  },
  "technicalDetails": {
    "title": "Tekniset ominaisuudet",
    "subtitle": "Mukautettava tunnistus eurooppalaisille liikeasiakirjoille.",
    "items": [
      {
        "title": "Kielituki",
        "description": "Esikoulutetut NLP-mallit saksalle, englannille, ranskalle, italialle ja espanjalle. Kielikohtainen entiteettien tunnistus takaa tarkan henkilötietojen tunnistuksen eurooppalaisissa liiketoimintaympäristöissä.",
        "tone": "primary"
      },
      {
        "title": "Mukautettavat PII-entiteetit",
        "description": "Valitse, mitkä henkilötietotyypit tunnistetaan ja anonymisoidaan: henkilöiden nimet, sähköpostiosoitteet, puhelinnumerot, postiosoitteet, IBAN-tilinumerot, luottokorttinumerot, sosiaaliturvatunnukset, verotunnisteet, passinumerot ja paljon muuta.",
        "tone": "info"
      },
      {
        "title": "Anonymisointitilat",
        "description": "Replace-tila korvaa henkilötiedot yleisillä paikkamerkeillä vaatimustenmukaisuuden dokumentointia varten. Fake-tila käyttää testiympäristöissä uskottavia korvaavia arvoja ja säilyttää dokumentin rakenteen.",
        "tone": "warning"
      },
      {
        "title": "Esi- ja jälkikäsittely",
        "description": "Toteuta omaa logiikkaa ennen anonymisointia ja sen jälkeen. Lisää valmiiden rajapintojen kautta dokumenttien validointi, muotomuunnokset, laatutarkistukset, auditointilokitus tai integraatio jatkojärjestelmiin.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat täydentää Kyveiliä tekoälypohjaisella verkkochatilla",
    "subtitle": "Uusi sovellus käyttää samaa anonymisointitapaa ennen varsinaista keskustelua ulkoisten tai sisäisten tekoälyjärjestelmien kanssa.",
    "eyebrow": "Uusi sovellus",
    "name": "AnonChat",
    "description": "AnonChat on tekoälypohjainen verkkochat, jossa Word- ja Excel-dokumentit voidaan anonymisoida ennen kuin niitä käytetään ChatGPT:n, Clauden, Geminin tai sisäisten LLM-mallien kanssa. Esikatselu näyttää anonymisoidun tekstin, ennen kuin sisältö siirretään chattiin.",
    "statement": "Päätös säilyy käyttäjällä: tarkista, hyväksy ja keskustele vasta sitten.",
    "steps": [
      {
        "title": "Lataa dokumentti",
        "description": "Word- tai Excel-tiedostot valmistellaan anonymisointia varten, ennen kuin ne siirtyvät tekoälytyönkulkuun.",
        "tone": "primary"
      },
      {
        "title": "Anonymisoi henkilötiedot",
        "description": "Kyveil tunnistaa henkilötiedot ja korvaa tai peittää ne valittujen sääntöjen mukaisesti.",
        "tone": "info"
      },
      {
        "title": "Tarkista esikatselu",
        "description": "Anonymisoitu teksti on nähtävissä, ennen kuin sisältö luovutetaan chatille.",
        "tone": "warning"
      },
      {
        "title": "Käytä tekoälychattia",
        "description": "Anonymisoitua sisältöä käytetään ulkoisten tai sisäisten LLM-mallien kanssa vasta tarkistuksen jälkeen.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Käyttöönottovaihtoehdot",
    "subtitle": "Valitse käyttötapaukseesi sopiva ratkaisu.",
    "options": [
      {
        "title": "Työpöytäsovellus",
        "subtitle": "Itsenäinen sovellus yksittäisille työasemille",
        "description": "Työpöytäversio toimii täysin itsenäisesti Windowsissa, macOS:ssä tai Linuxissa. Ihanteellinen käyttäjille, joiden on anonymisoitava dokumentteja suoraan omalla työpisteellään ilman verkkoriippuvuuksia.",
        "features": [
          "Toimii täysin offline-tilassa – internetyhteyttä ei tarvita",
          "Tiedostot eivät koskaan poistu paikalliselta työasemalta",
          "Eräkäsittely usealle tiedostolle",
          "Graafinen käyttöliittymä helppoa käyttöä varten",
          "Toimii tavallisilla toimistotietokoneilla ilman GPU:ta",
          "Kertalisenssi – ei tilausmallia"
        ]
      },
      {
        "title": "REST API",
        "subtitle": "Palvelinasennus integrointiin ja skaalaukseen",
        "description": "API-versio otetaan käyttöön omilla palvelimillasi tai yksityisessä pilvi-infrastruktuurissa. Ihanteellinen, kun anonymisointi halutaan integroida olemassa oleviin järjestelmiin ja työnkulkuihin, joissa käsittelymäärät ovat suuria.",
        "features": [
          "Käyttöönotto omassa infrastruktuurissa – omassa konesalissa (on-premises) tai yksityisessä pilvessä",
          "Horisontaalinen skaalaus suuriin läpimenovaatimuksiin",
          "RESTful-rajapinta ja kattava dokumentaatio",
          "Sama anonymisointimoottori kuin työpöytäversiossa",
          "Integraatioesimerkit yleisimmille alustoille",
          "Tuki esi- ja jälkikäsittelyn mukautuksille"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Tekoälypohjainen verkkochat, jossa anonymisointi tehdään ennen keskustelua",
        "description": "AnonChat sijoittaa Kyveilin varsinaisen tekoälychatin eteen. Word- ja Excel-dokumentit anonymisoidaan, esikatselu tarkistetaan, ja vasta sitten sisältöä käytetään ChatGPT:n, Clauden, Geminin tai sisäisten LLM-mallien kanssa.",
        "features": [
          "Anonymisointi ennen chattia jälkikäteisen valvonnan sijaan",
          "Anonymisoidun tekstin esikatselu ennen luovutusta",
          "Suunniteltu dokumenttipohjaisiin kysymyksiin tekoälyjärjestelmille",
          "Pienentää riskiä, että henkilötietoja lähetetään ulkoisille malleille",
          "Voidaan liittää sisäisiin LLM- tai yrityschat-ympäristöihin",
          "Käyttää samaa anonymisointitapaa kuin työpöytäsovellus ja REST API"
        ]
      }
    ],
    "bothNote": "Työpöytäsovellus, REST API ja AnonChat on suunniteltu anonymisoimaan arkaluonteinen sisältö ennen jatkokäsittelyä."
  },
  "useCases": {
    "title": "Käyttötapaukset",
    "subtitle": "GDPR-vaatimustenmukaisuudesta turvalliseen kehitysdataan.",
    "items": [
      {
        "title": "GDPR-vaatimustenmukaisuus",
        "description": "Anonymisoi henkilötiedot ennen kuin dokumentteja jaetaan organisaation ulkopuolelle tai sisäisille osastoille. Täytä tietosuojavaatimukset testidatan, analyysiaineistojen ja dokumentaation osalta.",
        "tone": "primary"
      },
      {
        "title": "Testidatan valmistelu",
        "description": "Valmistele realistiset testiaineistot niin, että kehittäjät voivat työskennellä tuotantoa vastaavalla datalla paljastamatta todellisia henkilötietoja.",
        "tone": "info"
      },
      {
        "title": "HR-dokumenttien käsittely",
        "description": "Anonymisoi työhakemukset, henkilöstökansiot ja HR-dokumentaatio puolueettomia arviointeja, vaatimustenmukaisuusauditointeja tai data-analyyseja varten yksityisyyttä suojaten.",
        "tone": "warning"
      },
      {
        "title": "Data-analyysi",
        "description": "Mahdollista henkilötietoja sisältävien dokumenttien analysointi anonymisoimalla arkaluonteiset tiedot ensin. Jaa aineistoja tiimien välillä ilman tietosuojahuolia.",
        "tone": "success"
      },
      {
        "title": "Dokumenttien arkistointi",
        "description": "Valmistele dokumentit pitkäaikaisarkistointiin poistamalla tai anonymisoimalla henkilötiedot säilytyskäytäntöjen ja tietosuojasäännösten mukaisesti.",
        "tone": "secondary"
      },
      {
        "title": "Turvallinen jakaminen",
        "description": "Jaa dokumentteja ulkoisille kumppaneille, alihankkijoille tai muille osastoille sen jälkeen, kun arkaluonteiset henkilötiedot on poistettu automaattisesti.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Versiot ja tuki",
    "subtitle": "Päivitykset, laajennukset ja tuki tuotantokäyttöön.",
    "items": [
      {
        "title": "Työpöytäsovellus, API ja AnonChat",
        "description": "Kyveil on saatavilla työpöytäsovelluksena ja REST API:na integraatioita varten. AnonChat on verkkochat, jossa dokumentit anonymisoidaan ennen keskustelua.",
        "tone": "info"
      },
      {
        "title": "Tuki",
        "description": "Yrityskäyttöön tarjoamme teknistä tukea, apua käyttöönotossa ja räätälöintiä.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Aloita Kyveilin käyttö",
    "subtitle": "Suojaa arkaluonteiset dokumentit ennen kuin ne päätyvät tekoälylle, testeihin, analyyseihin tai jaettavaksi.",
    "desktop": "Tiedustele työpöytäsovellusta",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Ota yhteyttä REST API -käyttöönotosta",
    "contact": "Kirjoita meille, jos haluat demon tai tarjouksen.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST API -käyttöönotto",
    "contactSubject": "Kyveil: demo tai tarjous"
  }
};
